import { useEffect, useMemo, useState, useRef } from "react";
import SearchInput from "./shared/SearchInput";
import { fetchTimesheetsMonth, fetchTkEmailCandidates,
  tkReportPreview, tkReportSend,fetchTkCcDefault, saveTkCcDefault,fetchTimesheetDatesNeeded } from "../utils/api";

/* ============================================================================
 * TimeKeeping — the Timesheets table, by month.
 *   · month navigation (monthYear "Jun2026" sent to the backend)
 *   · search by name or site
 *   · Monday–Sunday chips filtering the Day field (multi-select)
 *   · four clickable status totals that filter the table:
 *       Checked Out Correctly | Force Check Out | Not Checked Out |
 *       Under Schedule Hours
 *   · rows grouped by Date with a day header, EmployeePayments-style
 * ==========================================================================*/

const MONTHS3 = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];

const toneColor = (t) =>
  t === "red" ? "#f87171" : t === "green" ? "#4ade80" : t === "amber" ? "#fbbf24" : "#94a3b8";

const S = {
  backdrop: { position:"fixed", inset:0, top: "var(--fr-modal-top, 0px)", background:"rgba(2,6,23,0.7)", zIndex:1100,
    display:"flex", alignItems:"flex-start", justifyContent:"center", padding:"40px 16px", overflowY:"auto" },
  modal: { position:"relative", overflow:"hidden", width:"100%", maxWidth:1200,
    background:"linear-gradient(135deg,#1e293b 0%,#0f172a 100%)",
    border:"1px solid rgba(34,211,238,0.25)", borderRadius:16, padding:"18px 18px 14px" },
  accent: { position:"absolute", top:0, left:0, right:0, height:3,
    background:"linear-gradient(90deg,#22d3ee,#3b82f6,#8b5cf6)" },
  head: { display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 },
  title: { margin:0, fontSize:"1rem", fontWeight:700, color:"#f1f5f9" },
  closeX: { background:"none", border:"none", color:"rgba(255,255,255,0.6)", fontSize:"1.4rem", lineHeight:1, cursor:"pointer" },
  nav: { display:"flex", alignItems:"center", justifyContent:"center", gap:14, margin:"2px 0 10px" },
  navBtn: { width:32, height:32, borderRadius:9, border:"1px solid rgba(34,211,238,0.4)",
    background:"rgba(34,211,238,0.1)", color:"#22d3ee", cursor:"pointer", fontSize:"0.95rem", lineHeight:1 },
  navLabel: { minWidth:120, textAlign:"center", fontWeight:800, color:"#f1f5f9", fontSize:"0.95rem", letterSpacing:"0.02em" },
  chipsRow: { display:"flex", flexWrap:"wrap", gap:6, margin:"8px 0" },
  dayChip: (on) => ({ padding:"4px 12px", borderRadius:999, fontSize:"0.72rem", fontWeight:700, cursor:"pointer",
    border:`1px solid ${on ? "rgba(59,130,246,0.7)" : "rgba(255,255,255,0.15)"}`,
    background: on ? "rgba(59,130,246,0.2)" : "rgba(255,255,255,0.03)",
    color: on ? "#93c5fd" : "rgba(255,255,255,0.55)" }),
  buttonsGrid: { display:"grid", gridTemplateColumns:"repeat(4,minmax(0,1fr))", gap:10, margin:"10px 0 6px" },
  countBtn: (active) => ({ borderRadius:12, padding:"11px 8px", cursor:"pointer",
    background: active ? "rgba(34,211,238,0.15)" : "rgba(34,211,238,0.08)",
    border:`1px solid ${active ? "rgba(34,211,238,0.6)" : "rgba(34,211,238,0.25)"}`,
    display:"flex", flexDirection:"column", alignItems:"center", gap:4 }),
  countLabel: { fontSize:"0.62rem", color:"rgba(255,255,255,0.55)", textTransform:"uppercase",
    letterSpacing:"0.05em", fontWeight:700, textAlign:"center", lineHeight:1.3 },
  countValue: (tone) => ({ fontSize:"1.55rem", fontWeight:800, lineHeight:1, color: toneColor(tone) }),
  card: { background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)",
    borderRadius:12, padding:"12px", marginTop:8 },
  dateHead: { display:"flex", justifyContent:"space-between", alignItems:"baseline",
    padding:"10px 8px 6px", position:"sticky", top:0, background:"#1b2537", zIndex:1 },
  dateTitle: { fontSize:"0.78rem", fontWeight:800, color:"#22d3ee", letterSpacing:"0.04em" },
  dateMeta: { fontSize:"0.7rem", color:"rgba(255,255,255,0.45)" },
  colHead: { fontSize:"0.62rem", letterSpacing:"0.05em", textTransform:"uppercase",
    color:"rgba(255,255,255,0.45)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  cell: { fontSize:"0.82rem", color:"#e2e8f0", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  name: { fontSize:"0.84rem", fontWeight:700, color:"#22d3ee", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  badge: (kind) => ({ fontSize:"0.6rem", fontWeight:700, letterSpacing:"0.03em",
    padding:"1px 7px", borderRadius:999, whiteSpace:"nowrap",
    background: kind === "ok" ? "rgba(74,222,128,0.12)" : kind === "forced" ? "rgba(251,191,36,0.14)" : "rgba(248,113,113,0.12)",
    color: kind === "ok" ? "#4ade80" : kind === "forced" ? "#fbbf24" : "#f87171",
    border: `1px solid ${kind === "ok" ? "rgba(74,222,128,0.35)" : kind === "forced" ? "rgba(251,191,36,0.4)" : "rgba(248,113,113,0.4)"}` }),
  empty: { padding:"24px", textAlign:"center", color:"rgba(255,255,255,0.5)", fontStyle:"italic", fontSize:"0.85rem" },
  closeBtn: { width:"100%", padding:"11px", borderRadius:10, border:"none",
    background:"rgba(255,255,255,0.06)", color:"#e2e8f0", fontWeight:600, cursor:"pointer", marginTop:12 },
  gapCard: (behind) => ({ position:"relative", overflow:"hidden", width:"100%", maxWidth:1200,
    margin:"14px auto 24px", borderRadius:16, padding:"14px 18px",
    background:"linear-gradient(135deg,#1e293b 0%,#0f172a 100%)",
    border:`1px solid ${behind ? "rgba(251,191,36,0.4)" : "rgba(74,222,128,0.35)"}` }),
  gapTitle: { margin:"0 0 6px", fontSize:"0.85rem", fontWeight:800, color:"#f1f5f9" },
  gapLine: { fontSize:"0.78rem", color:"rgba(255,255,255,0.7)", lineHeight:1.6 },
  gapDates: { display:"flex", flexWrap:"wrap", gap:6, margin:"8px 0" },
  gapLink: { display:"inline-flex", alignItems:"center", gap:6, marginTop:8, padding:"7px 14px",
    borderRadius:9, fontSize:"0.78rem", fontWeight:700, textDecoration:"none",
    background:"rgba(34,211,238,0.1)", border:"1px solid rgba(34,211,238,0.4)", color:"#22d3ee" },
};

const TABLE_COLS = "1.3fr 1.1fr 0.9fr 0.9fr 0.6fr 0.6fr 1fr";

/* ---------- nested overlay styles (recipients + send) ---------- */
const O = {
  overlay: { position:"fixed", inset:0, top: "var(--fr-modal-top, 0px)", background:"rgba(0,0,0,0.75)", backdropFilter:"blur(6px)",
    display:"flex", alignItems:"center", justifyContent:"center", zIndex:1200, padding:20 },
  modal: { background:"linear-gradient(135deg,#0f172a 0%,#1e293b 100%)", border:"1px solid rgba(34,211,238,0.25)",
    borderRadius:16, width:"100%", maxWidth:640, overflow:"hidden", boxShadow:"0 25px 80px rgba(0,0,0,0.6)" },
  bar: { height:3, background:"linear-gradient(90deg,#22d3ee,#3b82f6,#8b5cf6)" },
  header: { padding:"16px 22px 8px", display:"flex", justifyContent:"space-between", alignItems:"center" },
  hTitle: { fontSize:"1rem", fontWeight:700, color:"#f1f5f9", margin:0 },
  body: { padding:"8px 22px 4px", maxHeight:"min(60vh, calc(100dvh - var(--fr-modal-top, 0px) - 200px))", overflowY:"auto" },
  footer: { padding:"14px 22px", display:"flex", justifyContent:"flex-end", gap:10,
    background:"rgba(0,0,0,0.2)", borderTop:"1px solid rgba(255,255,255,0.06)" },
  cancel: { background:"rgba(255,255,255,0.06)", color:"rgba(255,255,255,0.7)", border:"1px solid rgba(255,255,255,0.1)",
    borderRadius:10, padding:"10px 18px", fontSize:"0.85rem", fontWeight:600, cursor:"pointer" },
  confirm: (busy) => ({ background:"linear-gradient(135deg,#22d3ee 0%,#3b82f6 100%)", color:"#0f172a", border:"none",
    borderRadius:10, padding:"10px 20px", fontSize:"0.85rem", fontWeight:700,
    cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1,
    display:"inline-flex", alignItems:"center", gap:8 }),
  row: { display:"grid", gridTemplateColumns:"1.2fr 1fr 1.4fr 40px", alignItems:"center",
    columnGap:8, padding:"9px 4px", borderBottom:"1px solid rgba(255,255,255,0.06)" },
  rName: { fontSize:"0.85rem", fontWeight:700, color:"#22d3ee", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" },
  rCell: { fontSize:"0.8rem", color:"#e2e8f0", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" },
  pill: { fontSize:"0.72rem", color:"rgba(255,255,255,0.55)" },
  check: { width:16, height:16, accentColor:"#22d3ee", cursor:"pointer" },
  note: { padding:"8px 12px", borderRadius:8, margin:"8px 0", fontSize:"0.78rem",
    background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.35)", color:"#4ade80" },
  err: { padding:"8px 12px", borderRadius:8, margin:"8px 0", fontSize:"0.78rem",
    background:"rgba(248,113,113,0.08)", border:"1px solid rgba(248,113,113,0.35)", color:"#fca5a5" },
};

const Spin = () => (
  <span style={{ width:12, height:12, border:"2px solid currentColor", borderTopColor:"transparent",
    borderRadius:"50%", display:"inline-block", animation:"tk-spin 0.7s linear infinite" }} />
);

/* ---------- send report ---------- */
const SendModal = ({ filterHeader, reportRows,summary, onClose }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState({});
  const [busy, setBusy] = useState("");             // "preview" | "send"
  const [note, setNote] = useState("");
  const [error, setError] = useState("");

 


  const CC_CATEGORIES = [
  { key: "supervisor", label: "Supervisors" },
  { key: "admin",      label: "Admin" },
  { key: "operations", label: "Operations" },
];
const catOf = (r) => r.category || "operations";

const [chips, setChips] = useState([]);

useEffect(() => {
  Promise.all([fetchTkEmailCandidates(), fetchTkCcDefault()]).then(([cand, def]) => {
    const candidates = cand.status === "success" ? cand.candidates || [] : [];
    const active = def.status === "success" && Array.isArray(def.chips) ? def.chips : ["supervisor"];
    setList(candidates);
    setChips(active);
    const pre = {};
    candidates.forEach((r) => { if (active.includes(catOf(r))) pre[r.email] = true; });
    setChecked(pre);
    setLoading(false);
  });
}, []);

const toggleChip = (key) => {
  const next = chips.includes(key) ? chips.filter((c) => c !== key) : [...chips, key];
  setChips(next);
  const pre = {};
  list.forEach((r) => { if (next.includes(catOf(r))) pre[r.email] = true; });
  setChecked(pre);                      // chip toggle resets ticks to the categories
  saveTkCcDefault(next);                // fire-and-forget — new default persisted
};
  const allChecked = list.length > 0 && list.every((r) => checked[r.email]);
  const toggleAll = () => {
    const next = {};
    if (!allChecked) list.forEach((r) => { next[r.email] = true; });
    setChecked(next);
  };
  const recipients = list.filter((r) => checked[r.email])
  .map((r) => ({ name: r.name, email: r.email }));

  const preview = async () => {
    setBusy("preview"); setError(""); setNote("");
    const res = await tkReportPreview(filterHeader, reportRows, summary);
    if (res.status === "success") {
      window.open(URL.createObjectURL(res.blob), "_blank");
    } else setError(res.error || "Preview failed");
    setBusy("");
  };

  const send = async () => {
  setBusy("send"); setError(""); setNote("");
  const res = await tkReportSend(filterHeader, reportRows, recipients, summary);
  if (res.status === "success") {
    setNote(`Sent — ${recipients.length} personalised email${recipients.length === 1 ? "" : "s"} + work inbox copy.`);
  } else setError(res.error || "Send failed");
  setBusy("");
};

  return (
    <div style={O.overlay} onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onClose(); }}>
      <div style={O.modal}>
        <div style={O.bar} />
        <div style={O.header}>
          <h5 style={O.hTitle}>✉ Send TimeKeeping Report</h5>
          <span style={O.pill}>{reportRows.length} shifts in filter</span>
        </div>
        <div style={O.body}>
          <div style={{ ...O.pill, margin:"2px 0 8px" }}>{filterHeader}</div>
          <div style={{ display:"flex", gap:6, margin:"4px 0 10px" }}>
          {CC_CATEGORIES.map((c) => (
            <button key={c.key} type="button" style={S.dayChip(chips.includes(c.key))}
              onClick={() => toggleChip(c.key)}>
              {c.label}
            </button>
          ))}
        </div>
          {note && <div style={O.note}>{note}</div>}
          {error && <div style={O.err}>{error}</div>}
          <div style={{ ...O.row, gridTemplateColumns:"28px 1.2fr 1fr 1.4fr",
            borderBottom:"1px solid rgba(255,255,255,0.12)" }}>
            <input type="checkbox" style={O.check} checked={allChecked} onChange={toggleAll}
              title="Select all" />
            {["Name", "Job", "Email"].map((h) => (
              <span key={h} style={{ fontSize:"0.62rem", textTransform:"uppercase",
                letterSpacing:"0.05em", color:"rgba(255,255,255,0.45)", fontWeight:700 }}>{h}</span>
            ))}
          </div>
          {loading ? (
            <div style={{ padding:20, textAlign:"center", color:"rgba(255,255,255,0.5)" }}>Loading…</div>
          ) : list.map((r) => (
            <div key={r.email} style={{ ...O.row, gridTemplateColumns:"28px 1.2fr 1fr 1.4fr" }}>
              <input type="checkbox" style={O.check} checked={!!checked[r.email]}
                onChange={() => setChecked((p) => ({ ...p, [r.email]: !p[r.email] }))} />
              <span style={O.rName}>{r.name}</span>
              <span style={O.rCell}>{r.job || "—"}</span>
              <span style={O.rCell}>{r.email}</span>
            </div>
          ))}
        </div>
        <div style={O.footer}>
          <button type="button" style={O.cancel} onClick={onClose}>Close</button>
          <button type="button" style={O.confirm(busy === "preview")} onClick={preview}
            disabled={!!busy || reportRows.length === 0}>
            {busy === "preview" ? <Spin /> : "👁"} Preview Report
          </button>
          <button type="button" style={O.confirm(busy === "send")} onClick={send}
            disabled={!!busy || reportRows.length === 0}>
           {busy === "send" ? <Spin /> : "✉"} Send Emails ({recipients.length})
          </button>
        </div>
      </div>
    </div>
  );
};

/* row status: how did this shift end? */
const statusOf = (r) => {
  if (!r["Actual End"]) return "notout";                       // never checked out
  if (r["Forgot Checkout"]) return "forced";                   // bot force-closed it
  return "ok";
};
const isUnder = (r) =>
  Number(r["Adjusted Hours"] || 0) < Number(r["Scheduled Hours"] || 0);

const FILTERS = [
  { key:"ok",     label:"Checked Out Correctly", tone:"green", test:(r) => statusOf(r) === "ok" },
  { key:"forced", label:"Force Check Out",       tone:"amber", test:(r) => statusOf(r) === "forced" },
  { key:"notout", label:"Not Checked Out",       tone:"red",   test:(r) => statusOf(r) === "notout" },
  { key:"under",  label:"Under Schedule Hours",  tone:"red",   test:isUnder },
];

const TimeKeeping = ({ onClose, refreshKey = 0, onBusy }) => {
  const inline = !onClose;                    // rendered inside a tab, not as a modal
  const now = new Date();
  const [refreshing, setRefreshing] = useState(false);         // ↻ soft refresh
  const [month, setMonth] = useState(now.getMonth());          // 0-11
  const [year, setYear] = useState(now.getFullYear());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [days, setDays] = useState([]);                        // active day chips
  const [filters, setFilters] = useState([]);                  // status button
  const [sendOpen, setSendOpen] = useState(false);
  const [dates, setDates] = useState([]);              // selected ISO dates
  const dateRef = useRef(null);
  const [gap, setGap] = useState(null);
  const [gapLoading, setGapLoading] = useState(true);
  const monthYear = `${MONTHS3[month]}${year}`;                // "Jun2026"
  const mm = String(month + 1).padStart(2, "0");
  const lastDay = new Date(year, month + 1, 0).getDate();
  const dateMin = `${year}-${mm}-01`;
  const dateMax = `${year}-${mm}-${String(lastDay).padStart(2, "0")}`;
  useEffect(() => {
  let alive = true;
  (async () => {
    setLoading(true);
    const res = await fetchTimesheetsMonth(monthYear);
    if (alive) {
      setRows(res.status === "success" ? res.rows || [] : []);
      setLoading(false);
    }
  })();
  return () => { alive = false; };
}, [monthYear]);

useEffect(() => {
  fetchTimesheetDatesNeeded().then((res) => {
    if (res.status === "success") setGap(res);
    setGapLoading(false);
  });
}, []);

/* soft refresh — refetch the same month + coverage in place: keeps the table
   visible and every day chip, date, filter and search exactly as they are */
const refresh = async () => {
  if (refreshing || loading) return;
  setRefreshing(true);
  try {
    const [res, gapRes] = await Promise.all([fetchTimesheetsMonth(monthYear), fetchTimesheetDatesNeeded()]);
    if (res.status === "success") setRows(res.rows || []);
    if (gapRes.status === "success") setGap(gapRes);
  } catch { /* keep current rows on a failed refresh */ }
  setRefreshing(false);
};

useEffect(() => { if (refreshKey) refresh(); }, [refreshKey]);   // eslint-disable-line react-hooks/exhaustive-deps
useEffect(() => { onBusy?.(loading || refreshing); }, [loading, refreshing, onBusy]);


/* dates missing WITHIN the displayed month, up to yesterday (Melbourne ≈ local) */
const monthMissing = useMemo(() => {
  if (loading || !rows.length) return null;          // empty month -> global line covers it
  const have = new Set(rows.map((r) => r["Date"]));
  const now = new Date();
  const yest = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  const missing = [];
  for (let d = 1; d <= lastDay; d++) {
    const dt = new Date(year, month, d);
    if (dt > yest) break;                            // future days aren't "missing"
    const iso = `${year}-${mm}-${String(d).padStart(2, "0")}`;
    if (!have.has(iso)) missing.push(iso);
  }
  return missing;
}, [rows, loading, year, month, mm, lastDay]);
  useEffect(() => {
    if (inline) return;                       // no Escape-to-close inside a tab
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, inline]);

  const nav = (d) => {
    let m = month + d, y = year;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setMonth(m); setYear(y); setFilters([]); setDates([]);
  };
const fmtDate = (iso) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const yesterdayPreset = () => {
  const y = new Date();
  y.setDate(y.getDate() - 1);
  const iso = `${y.getFullYear()}-${String(y.getMonth() + 1).padStart(2, "0")}-${String(y.getDate()).padStart(2, "0")}`;
  setMonth(y.getMonth()); setYear(y.getFullYear());   // crosses month boundary on the 1st
  setDates([iso]);
  setFilters(["forced", "notout"]);
  setDays([]); setQuery("");
};
  /* chips+search scope (status counts compute on this) */
  const scoped = useMemo(() => {
    const q = query.toLowerCase().replace(/\s+/g, "");
    return rows.filter((r) => {
  if (dates.length && !dates.includes(r["Date"])) return false;
  if (days.length && !days.includes(r["Day"])) return false;
  
      if (q) {
        const hay = `${r["Employee Name"]}${r["System Name"]}${r["Site Name"]}`
          .toLowerCase().replace(/\s+/g, "");
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, days, dates, query]);

  const counts = useMemo(() => {
    const c = {};
    FILTERS.forEach((f) => { c[f.key] = scoped.filter(f.test).length; });
    return c;
  }, [scoped]);

  const visible = useMemo(() => {
  if (!filters.length) return scoped;
  const active = FILTERS.filter((f) => filters.includes(f.key));
  return scoped.filter((r) => active.some((f) => f.test(r)));
}, [scoped, filters]);

  /* group by Date, ascending */
  const groups = useMemo(() => {
    const g = {};
    visible.forEach((r) => { (g[r["Date"]] = g[r["Date"]] || []).push(r); });
    return Object.keys(g).sort().map((d) => ({
      date: d,
      day: g[d][0]?.["Day"] || "",
      hours: g[d].reduce((s, r) => s + Number(r["Adjusted Hours"] || 0), 0),
      rows: g[d].sort((a, b) => (a["Employee Name"] || "").localeCompare(b["Employee Name"] || "")),
    }));
  }, [visible]);



  const summary = useMemo(() => ({
  totalShifts: scoped.length,
  totalHours: scoped.reduce((s, r) => s + Number(r["Adjusted Hours"] || 0), 0),
  counts,                                  // ok/forced/notout/under from scoped
}), [scoped, counts]);



  const filterHeader = useMemo(() => {
  const parts = [`${MONTHS3[month]} ${year}`];
  if (days.length) parts.push(`Days: ${days.map((d) => d.slice(0, 3)).join(", ")}`);
  if (dates.length) parts.push(`Dates: ${dates.map(fmtDate).join(", ")}`);
  if (query.trim()) parts.push(`Search: "${query.trim()}"`);
  const act = FILTERS.filter((x) => filters.includes(x.key)).map((x) => x.label);
  parts.push(act.length ? `Filter: ${act.join(" + ")}` : "Filter: All shifts");
  return parts.join(" · ");
}, [month, year, days,dates, query, filters]);

  

  const shell = (
    <>
      <style>{`@keyframes tk-spin{to{transform:rotate(360deg)}}
        .tk-row{transition:background .12s ease}.tk-row:hover{background:rgba(34,211,238,0.06)}`}</style>
      <div style={{ ...S.modal, margin: inline ? "0 auto" : undefined }}
        onClick={(e) => e.stopPropagation()}>
        <div style={S.accent} />
        <div style={S.head}>
          <h4 style={S.title}>Time Keeping Dashboard</h4>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <button type="button" style={{ ...S.navBtn, opacity: refreshing ? 0.6 : 1 }}
              title="Refresh data — keeps current filters" onClick={refresh}>
              {refreshing ? <Spin /> : "↻"}
            </button>
            <button type="button" style={S.navBtn} title="Yesterday's exceptions — force check outs & not checked out"
              onClick={yesterdayPreset}>⚡</button>
            
          <button type="button" style={S.navBtn} title="Send report for the current filter"
            onClick={() => setSendOpen(true)}>✉</button>
          
            {!inline && (
              <button type="button" style={S.closeX} onClick={onClose} aria-label="Close">×</button>
            )}
          </div>
        </div>

        {/* month navigation */}
        <div style={S.nav}>
          <button type="button" style={S.navBtn} onClick={() => nav(-1)}>◄</button>
          <span style={S.navLabel}>{MONTHS3[month]} {year}</span>
          <button type="button" style={S.navBtn} onClick={() => nav(1)}>►</button>
        </div>

        <SearchInput value={query} onChange={setQuery} placeholder="Search name or site…" />

        {/* Monday–Sunday chips (multi-select; none selected = all days) */}
       <div style={S.chipsRow}>
        {DAYS.map((d) => (
          <button key={d} type="button" style={S.dayChip(days.includes(d))}
            onClick={() => setDays((p) => p.includes(d) ? p.filter((x) => x !== d) : [...p, d])}>
            {d.slice(0, 3)}
          </button>
        ))}
        <button type="button" style={S.dayChip(dates.length > 0)} title="Filter specific dates"
          onClick={() => dateRef.current?.showPicker?.() || dateRef.current?.click()}>
          📅
        </button>
        <input ref={dateRef} type="date" min={dateMin} max={dateMax}
          style={{ position:"absolute", opacity:0, pointerEvents:"none", width:0, height:0 }}
          onChange={(e) => {
            const v = e.target.value;
            if (v) setDates((p) => (p.includes(v) ? p : [...p, v]));
            e.target.value = "";
          }} />
        {dates.map((d) => (
          <button key={d} type="button" style={S.dayChip(true)} title="Remove"
            onClick={() => setDates((p) => p.filter((x) => x !== d))}>
            {fmtDate(d)} ×
          </button>
        ))}
      </div>

        {/* status totals — clickable filters */}
        <div style={S.buttonsGrid}>
          {FILTERS.map((f) => (
            <button key={f.key} type="button" style={S.countBtn(filters.includes(f.key))}
              onClick={() => setFilters((p) =>
                p.includes(f.key) ? p.filter((x) => x !== f.key) : [...p, f.key])}>
              <span style={S.countLabel}>{f.label}</span>
              <span style={S.countValue(f.tone)}>{loading ? "…" : counts[f.key]}</span>
            </button>
          ))}
        </div>

        {/* grouped table */}
        <div style={S.card}>
          {loading ? (
            <div style={S.empty}>Loading {MONTHS3[month]} {year}…</div>
          ) : groups.length === 0 ? (
            <div style={S.empty}>No timesheets for this view.</div>
          ) : (
            <div style={{ maxHeight:420, overflowY:"auto", overflowX:"auto" }}>
              <div style={{ minWidth:760 }}>
                {groups.map((g) => (
                  <div key={g.date}>
                    <div style={S.dateHead}>
                      <span style={S.dateTitle}>{g.day} · {fmtDate(g.date)}</span>
                      <span style={S.dateMeta}>
                        {g.rows.length} shift{g.rows.length === 1 ? "" : "s"} · {g.hours.toFixed(2)} hrs
                      </span>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:TABLE_COLS, padding:"4px 8px 8px",
                      columnGap:8, borderBottom:"1px solid rgba(255,255,255,0.12)" }}>
                      {["Name","Site","Scheduled","Actual","Sched Hrs","Adj Hrs","Status"].map((h) => (
                        <span key={h} style={S.colHead}>{h}</span>
                      ))}
                    </div>
                    {g.rows.map((r, i) => {
                      const st = statusOf(r);
                      return (
                        <div key={`${g.date}-${i}`} className="tk-row"
                          style={{ display:"grid", gridTemplateColumns:TABLE_COLS, alignItems:"center",
                            padding:"9px 8px", columnGap:8,
                            borderTop: i ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
                          <span style={S.name}>{r["System Name"] || r["Employee Name"]}</span>
                          <span style={S.cell}>{r["Site Name"]}</span>
                          <span style={S.cell}>{r["Schedule Start"]}–{r["Schedule End"] || "—"}</span>
                          <span style={S.cell}>{r["Actual Start"] || "—"}–{r["Actual End"] || "—"}</span>
                          <span style={S.cell}>{r["Scheduled Hours"]}</span>
                          <span style={{ ...S.cell, color: isUnder(r) ? "#f87171" : "#e2e8f0" }}>
                            {r["Adjusted Hours"]}
                          </span>
                          <span>
                            <span style={S.badge(st)}>
                              {st === "ok" ? "Checked Out" : st === "forced" ? "Force Closed" : "Not Checked Out"}
                            </span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {!inline && (
          <button type="button" style={S.closeBtn} onClick={onClose}>Close</button>
        )}
        {sendOpen && (
            <SendModal filterHeader={filterHeader} reportRows={visible} summary={summary}
              onClose={() => setSendOpen(false)} />
          )}
      </div>                   

     {gapLoading ? (
        <div style={S.gapCard(false)}>
          <div style={{ ...S.accent, opacity:0.6 }} />
          <h5 style={S.gapTitle}>📅 Timesheet Coverage</h5>
          <div style={{ ...S.gapLine, display:"flex", alignItems:"center", gap:8 }}>
            <Spin /> Checking last uploaded date…
          </div>
        </div>
      ) : gap && (
        <div style={S.gapCard(gap.needed?.length > 0 || (monthMissing?.length > 0))}>
          <div style={{ ...S.accent, opacity:0.6 }} />
          <h5 style={S.gapTitle}>📅 Timesheet Coverage</h5>

          {/* global layer */}
          {gap.needed?.length > 0 ? (
            <>
              <div style={S.gapLine}>
                Last date on the Timesheets table:{" "}
                <strong style={{ color:"#22d3ee" }}>
                  {gap.uploadedThrough ? fmtDate(gap.uploadedThrough) : "none yet"}
                </strong>
                {" — "}
                <strong style={{ color:"#fbbf24" }}>
                  {gap.needed.length} day{gap.needed.length === 1 ? "" : "s"} missing
                </strong>{" "}
                up to yesterday:
              </div>
              <div style={S.gapDates}>
                {gap.needed.map((d) => (
                  <span key={d} style={S.dayChip(false)}>{fmtDate(d)}</span>
                ))}
              </div>
            </>
          ) : (
            <div style={S.gapLine}>
              Up to date — Timesheets table covers through{" "}
              <strong style={{ color:"#4ade80" }}>
                {gap.uploadedThrough ? fmtDate(gap.uploadedThrough) : "—"}
              </strong>.
            </div>
          )}

          {/* month layer — independent of global state */}
          {monthMissing && monthMissing.length > 0 && (
            <>
              <div style={{ ...S.gapLine, marginTop: 6 }}>
                Within <strong style={{ color:"#22d3ee" }}>{MONTHS3[month]} {year}</strong>:{" "}
                <strong style={{ color:"#fbbf24" }}>
                  {monthMissing.length} day{monthMissing.length === 1 ? "" : "s"} missing
                </strong>
              </div>
              <div style={S.gapDates}>
                {monthMissing.map((d) => (
                  <span key={d} style={S.dayChip(false)}>{fmtDate(d)}</span>
                ))}
              </div>
            </>
          )}

          {/* link — when either layer has something to chase */}
          {(gap.needed?.length > 0 || monthMissing?.length > 0) && (
            <a href="https://app.freshops.com.au/account/login" target="_blank"
              rel="noopener noreferrer" style={S.gapLink}>
              ↗ Open Freshops
            </a>
          )}
        </div>
      )}
     </>
     );
  return inline ? shell : (
    <div style={S.backdrop} onClick={onClose}>{shell}</div>
  );
};

export default TimeKeeping;